'use client';

import { useMemo, useState } from 'react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '../../../ui/chart';
import { CHART_COLORS, ChatChartData } from './ChatChartComponent';
import ChatChartTablePreview from './ChatChartTablePreview';

interface LineChartProps {
    chartData: ChatChartData;
}

export const ChatLineChartComponent = ({ chartData }: LineChartProps) => {
    const [showMetadata, setShowMetadata] = useState(false);
    const { meta, data } = chartData;

    const { title, yAxisKey, xAxisName, yAxisName, xAxisKey } = useMemo(() => {



        const yAxisKey = meta?.y_axis_label ?? meta?.yAxis?.dataKey ?? meta?.y_axis?.dataKey ?? meta?.y_axis?.data_key ??  meta?.y_axis?.name ?? '';
        const yAxisName = meta?.y_axis_label ?? meta?.yAxis?.name ?? meta?.y_axis?.name ?? '';
        const xAxisName = meta?.x_axis_label ?? meta?.xAxis?.name ?? meta?.x_axis?.name ?? meta?.x_axis?.dataKey ?? meta?.x_axis?.data_key ?? '';
        const title = meta?.title ?? `${xAxisName ?? ''} vs ${yAxisName ?? ''}`;
        const xAxisKey = meta?.xAxis?.dataKey ?? meta?.x_axis?.dataKey ?? meta?.x_axis?.data_key ?? '';

        return {
            title,
            yAxisKey,
            xAxisName,
            xAxisKey,
            yAxisName,
        };
    }, [chartData]);

    if (!xAxisName || !yAxisName) {
        return <div>Missing axis configuration for LineChart</div>;
    }





    const margin = meta?.margin || { top: 20, right: 30, left: 20, bottom: 5 };

    const chartConfig: ChartConfig = {
        [xAxisKey]: {
            label: xAxisName,
            color: CHART_COLORS[0],
        },
        [yAxisKey]: {
            label: yAxisName,
            color: CHART_COLORS[1],
        },
    };

    return (
        <Card className="w-full shadow-md hover:shadow-lg transition-shadow duration-300">
            <CardHeader className="flex flex-row items-center justify-between">
                <div>
                    <CardTitle>Line Chart</CardTitle>
                    <CardDescription>
                        {title}
                    </CardDescription>
                </div>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowMetadata(!showMetadata)}
                    aria-label={showMetadata ? 'Hide data table' : 'Show data table'}
                >
                    {showMetadata ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </Button>
            </CardHeader>
            <CardContent>
                <ChartContainer config={chartConfig} className="min-h-[400px]">
                    <LineChart accessibilityLayer data={data} margin={margin}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={xAxisName}

                               label={{ value: xAxisName, position: 'insideBottom', offset: -5 }} />
                        <YAxis

                            label={{ value: yAxisName, angle: -90, position: 'insideLeft' }} />
                        <ChartTooltip content={<ChartTooltipContent />} />
                         {/*<Line type="monotone" dataKey={yAxisKey} stroke={CHART_COLORS[0]} strokeWidth={2}*/}
                         {/*                 activeDot={{ r: 8 }} />*/}
                        {

                            meta?.lines ? meta.lines.map((line: {
                                dataKey?: string,
                                data_key?: string,
                                name: string,
                                stroke?: string
                            }, index: number) => (
                                <Line type="monotone"
                                      activeDot={{r: 6}}
                                      key={line.dataKey as string || line.data_key as string}
                                      dataKey={line.dataKey as string || line.data_key as string}
                                      stroke={line.stroke || CHART_COLORS[index % CHART_COLORS.length]}
                                      strokeWidth={2} />
                            )) : null
                        }

                    </LineChart>
                </ChartContainer>

                <ChatChartTablePreview closeTable={() => {
                    setShowMetadata(false);
                }} showTable={showMetadata} data={data} />
            </CardContent>
        </Card>
    );
};

